import React from 'react';
import PhoneInput from 'react-phone-number-input/input';
import { DatePicker } from 'antd';
import moment from 'moment';
import {
    CheckIcon,
    ChevronDownIcon,
    DownloadIcon,
    InfoIcon,
} from 'lucide-react';
import { QUESTION_TYPES } from '../../../config/staffFormSchema';
import SignatureField from './SignatureField';
import FileUploadField from './FileUploadField';
import AgreementCheckboxField from './AgreementCheckboxField';
import VersionHint from './VersionHint';

const dateFormatNew = 'MM/DD/YYYY';
const disabledDate = current => current && (current > moment().startOf('day') || current < moment(`${new Date().getFullYear() - 100}-10-23`).startOf('day'));

const inputClasses = 'tw-w-full tw-rounded-lg tw-border tw-border-sand-300 tw-bg-white tw-px-3.5 tw-py-2.5 tw-text-sm tw-text-gray-900 placeholder:tw-text-gray-400 tw-transition focus:tw-border-brand-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-brand-500/20 disabled:tw-bg-sand-100';

/*
 * The version hint rides alongside the label on every branch below rather than above the field in
 * SectionCard, where it used to sit: out there it occupied flow height in the grid cell and
 * knocked a badged question out of alignment with the one beside it.
 *
 * `question` already carries `versionBadge`, so nothing new is threaded through to reach here.
 *
 * Deliberately a SIBLING of <label> rather than a child. The hint is a button, and interactive
 * content inside a label is both invalid and harmful: its aria-label gets folded into the
 * control's own accessible name, so the field would announce as "Locker number Why is this here?".
 * The flex row keeps it on the label's line, which is the whole point of moving it off the field.
 */
const Label = ({ question, fieldId }) => (
    <div className="tw-mb-1.5 tw-flex tw-items-center">
        <label htmlFor={fieldId} className="tw-text-sm tw-font-semibold tw-text-gray-800">
            {question.label}
            {question.required ? <span className="tw-ml-1 tw-text-brand-600">*</span> : <span className="tw-ml-1 tw-font-normal tw-text-gray-400">(optional)</span>}
        </label>
        <VersionHint badge={question.versionBadge} />
    </div>
);

const Help = ({ question }) => (question.helpText ? <p className="tw-mt-1.5 tw-text-xs tw-leading-relaxed tw-text-gray-500">{question.helpText}</p> : null);

// Every dropdown on this form is a native <select> carrying `inputClasses`, so it sits flush
// with the text inputs beside it. antd's <Select> cannot: the app's global
// `.ant-select-selector { border: 0 !important }` (main.css) strips its border outright and
// recolours its text, which is what made "Age group" look broken next to "Program name".
//
// `options` is [{ value, label }] — the fixed list off the question for a plain select, or the
// camp's programs for the campPrograms source.
const NativeSelect = ({ question, fieldId, options, value, onChange, error, disabled }) => (
    <div>
        <Label question={question} fieldId={fieldId} />
        <div className="tw-relative">
            <select
                id={fieldId}
                disabled={disabled}
                value={value || ''}
                onChange={e => onChange(e.target.value)}
                className={`${inputClasses} tw-appearance-none tw-pr-9`}>
                <option value="">{question.placeholder || 'Select an option'}</option>
                {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            <ChevronDownIcon className="tw-pointer-events-none tw-absolute tw-right-3 tw-top-1/2 tw-h-4 tw-w-4 -tw-translate-y-1/2 tw-text-gray-400" aria-hidden="true" />
        </div>
        {error ? <p className="tw-mt-1 tw-text-xs tw-text-brand-600">{error}</p> : null}
        <Help question={question} />
    </div>
);

// `context` carries runtime data/callbacks a handful of legacy fields need beyond the
// generic (question, value, onChange) contract: campDetail (agreement text / training guide
// PDF), programsList (dynamic Program Name options).
const QuestionField = ({ question, value, onChange, error, context = {}, disabled }) => {
    const fieldId = `field-${question.questionKey}`;

    if (question.questionKey === 'trainingGuidePdf') {
        return (
            <div className="tw-rounded-xl tw-border tw-border-sand-300 tw-bg-white tw-p-4 tw-text-gray-700">
                <div className="tw-flex tw-items-center tw-gap-2">
                    <InfoIcon className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden="true" />
                    <p className="tw-text-sm tw-font-semibold">{question.label}</p>
                </div>
                {context.campDetail?.trainingGuidePdf ? (
                    <a
                        target="_blank" rel="noreferrer" href={context.campDetail.trainingGuidePdf}
                        className="tw-mt-3 tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-brand-600 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-brand-700 tw-transition hover:tw-bg-brand-50">
                        <DownloadIcon className="tw-h-4 tw-w-4" aria-hidden="true" />
                        Download Training Guide PDF
                    </a>
                ) : <p className="tw-mt-2 tw-text-sm tw-text-gray-500">No training guide has been uploaded yet.</p>}
            </div>
        );
    }

    if (question.questionKey === 'readTrainingGuide') {
        return (
            <div className="tw-rounded-xl tw-border tw-border-sand-300 tw-bg-white tw-p-3.5">
                <AgreementCheckboxField
                    label={question.label}
                    value={value}
                    onChange={onChange}
                    agreementText={context.campDetail?.agreement}
                    required={question.required} />
            </div>
        );
    }

    if (question.type === QUESTION_TYPES.SIGNATURE) {
        return (
            <div>
                <Label question={question} fieldId={fieldId} />
                <SignatureField value={value} onChange={onChange} />
                <Help question={question} />
            </div>
        );
    }

    if (question.type === QUESTION_TYPES.INFO) {
        const toneClasses = question.tone === 'legal'
            ? 'tw-border-sand-300 tw-bg-sand-50 tw-text-gray-600'
            : question.tone === 'notice'
                ? 'tw-border-brand-200 tw-bg-brand-50 tw-text-brand-900'
                : 'tw-border-sand-300 tw-bg-white tw-text-gray-700';
        return (
            <div className={`tw-rounded-xl tw-border ${toneClasses} tw-p-4`}>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <InfoIcon className="tw-h-4 tw-w-4 tw-shrink-0" aria-hidden="true" />
                    <p className="tw-text-sm tw-font-semibold">
                        {question.label}
                        <VersionHint badge={question.versionBadge} />
                    </p>
                </div>
                {question.body ? <div className="tw-mt-2 tw-whitespace-pre-line tw-text-sm tw-leading-relaxed">{question.body}</div> : null}
            </div>
        );
    }

    if (question.optionsSource === 'campPrograms') {
        return (
            <NativeSelect
                question={question} fieldId={fieldId} value={value} onChange={onChange} error={error} disabled={disabled}
                options={(context.programsList || []).map(program => ({ value: program._id, label: program.programName }))} />
        );
    }

    switch (question.type) {
        case QUESTION_TYPES.SELECT:
            return (
                <NativeSelect
                    question={question} fieldId={fieldId} value={value} onChange={onChange} error={error} disabled={disabled}
                    options={(question.options || []).map(item => ({ value: item, label: item }))} />
            );

        case QUESTION_TYPES.TEXTAREA:
            return (
                <div>
                    <Label question={question} fieldId={fieldId} />
                    <textarea
                        id={fieldId}
                        rows={4}
                        disabled={disabled}
                        value={value || ''}
                        onChange={e => onChange(e.target.value)}
                        placeholder={question.placeholder}
                        className={`${inputClasses} tw-resize-y`} />
                    <Help question={question} />
                </div>
            );

        case QUESTION_TYPES.CHECKBOX:
            return (
                <div className="tw-rounded-xl tw-border tw-border-sand-300 tw-bg-white tw-p-3.5">
                    {/* Hint outside the <label> for the same reason as the shared Label above:
                        a button inside it would be folded into the checkbox's accessible name. */}
                    <div className="tw-flex tw-items-start">
                        <label htmlFor={fieldId} className="tw-flex tw-flex-1 tw-cursor-pointer tw-items-start tw-gap-3">
                            <input
                                id={fieldId}
                                type="checkbox"
                                disabled={disabled}
                                checked={Boolean(value)}
                                onChange={e => onChange(e.target.checked)}
                                className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-sand-300 tw-text-brand-600 focus:tw-ring-brand-500" />
                            <span className="tw-text-sm tw-leading-relaxed tw-text-gray-700">
                                {question.label}
                                {question.required ? <span className="tw-ml-1 tw-text-brand-600">*</span> : null}
                            </span>
                        </label>
                        <VersionHint badge={question.versionBadge} />
                    </div>
                    <Help question={question} />
                </div>
            );

        case QUESTION_TYPES.YESNO:
            return (
                <fieldset>
                    <legend className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-800">
                        {question.label}
                        {question.required ? <span className="tw-ml-1 tw-text-brand-600">*</span> : null}
                        <VersionHint badge={question.versionBadge} />
                    </legend>
                    <div className="tw-flex tw-gap-2">
                        {[{ label: 'Yes', code: 1 }, { label: 'No', code: 2 }].map(option => {
                            const selected = value === option.code;
                            return (
                                <button
                                    key={option.code}
                                    type="button"
                                    disabled={disabled}
                                    aria-pressed={selected}
                                    onClick={() => onChange(option.code)}
                                    className={`tw-inline-flex tw-min-w-[84px] tw-items-center tw-justify-center tw-gap-1.5 tw-rounded-lg tw-border tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-transition ${
                                        selected ? 'tw-border-brand-600 tw-bg-brand-600 tw-text-white' : 'tw-border-sand-300 tw-bg-white tw-text-gray-600 hover:tw-border-brand-300 hover:tw-text-brand-700'}`}>
                                    {selected ? <CheckIcon className="tw-h-3.5 tw-w-3.5" aria-hidden="true" /> : null}
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                    <Help question={question} />
                </fieldset>
            );

        case QUESTION_TYPES.CHECKLIST: {
            const selected = Array.isArray(value) ? value : [];
            return (
                <fieldset>
                    <legend className="tw-mb-2 tw-text-sm tw-font-semibold tw-text-gray-800">
                        {question.label}
                        {question.required ? <span className="tw-ml-1 tw-text-brand-600">*</span> : null}
                        {selected.length > 0 ? <span className="tw-ml-2 tw-rounded-full tw-bg-brand-50 tw-px-2 tw-py-0.5 tw-text-xs tw-font-semibold tw-text-brand-700">{selected.length} selected</span> : null}
                        <VersionHint badge={question.versionBadge} />
                    </legend>
                    <div className="tw-max-h-72 tw-space-y-1 tw-overflow-y-auto tw-rounded-xl tw-border tw-border-sand-300 tw-bg-white tw-p-2">
                        {(question.options || []).map(option => {
                            const isChecked = selected.includes(option);
                            return (
                                <label
                                    key={option}
                                    className={`tw-flex tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-lg tw-px-2.5 tw-py-2 tw-text-sm tw-transition ${isChecked ? 'tw-bg-brand-50 tw-text-brand-900' : 'tw-text-gray-700 hover:tw-bg-sand-100'}`}>
                                    <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => onChange(isChecked ? selected.filter(item => item !== option) : [...selected, option])}
                                        className="tw-mt-0.5 tw-h-4 tw-w-4 tw-shrink-0 tw-rounded tw-border-sand-300 tw-text-brand-600 focus:tw-ring-brand-500" />
                                    <span className="tw-leading-snug">{option}</span>
                                </label>
                            );
                        })}
                    </div>
                    <Help question={question} />
                </fieldset>
            );
        }

        case QUESTION_TYPES.FILE:
            return (
                <div>
                    <Label question={question} fieldId={fieldId} />
                    <FileUploadField value={value} onChange={onChange} />
                    <Help question={question} />
                </div>
            );

        case QUESTION_TYPES.DATE:
            return (
                <div>
                    <Label question={question} fieldId={fieldId} />
                    {/* Chrome comes from the scoped .ant-picker rules in staffApplication.tailwind.css,
                        not tw- utilities: antd styles .ant-picker itself, so border/padding/font
                        classes put here lose to it depending on stylesheet order. */}
                    <DatePicker
                        disabledDate={disabledDate}
                        disabled={disabled}
                        value={value ? moment(parseInt(value, 10)) : undefined}
                        className="!tw-w-full"
                        placeholder="MM/DD/YYYY"
                        format={dateFormatNew}
                        onChange={date => onChange(date ? new Date(date._d).getTime() : undefined)} />
                    <Help question={question} />
                </div>
            );

        case QUESTION_TYPES.TEL:
            return (
                <div>
                    <Label question={question} fieldId={fieldId} />
                    <PhoneInput country="US" className={inputClasses} maxLength={16} disabled={disabled} onChange={val => onChange(val)} value={value} />
                    {error ? <p className="tw-mt-1 tw-text-xs tw-text-brand-600">{error}</p> : null}
                    <Help question={question} />
                </div>
            );

        case QUESTION_TYPES.EMAIL:
        case QUESTION_TYPES.NUMBER:
        case QUESTION_TYPES.TEXT:
        default:
            return (
                <div>
                    <Label question={question} fieldId={fieldId} />
                    <input
                        id={fieldId}
                        type={question.type === QUESTION_TYPES.NUMBER ? 'number' : question.type === QUESTION_TYPES.EMAIL ? 'email' : 'text'}
                        disabled={disabled}
                        value={value || ''}
                        onChange={e => onChange(e.target.value)}
                        placeholder={question.placeholder}
                        className={inputClasses} />
                    {error ? <p className="tw-mt-1 tw-text-xs tw-text-brand-600">{error}</p> : null}
                    <Help question={question} />
                </div>
            );
    }
};

export default QuestionField;
